import { useLingui } from '@lingui/react/macro';
import { Check, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import { ActionBarButton, MobileActionStrip, MoreActionsMenu, splitActions, type ActionBarItem } from './action-bar';

export interface BulkBarProps {
  /** Quantos registros estão marcados; com 0 a barra some. */
  readonly count: number;
  /** "3 selecionadas" (gênero do registro). Padrão: "3 selecionados". */
  readonly countLabel?: string;
  /** Resumo da seleção, como o VOP (soma do valor da operação) da lista de crédito. */
  readonly summary?: ReactNode;
  /** Ações da seleção, já projetadas da tabela da lista (`actionBarItems`). */
  readonly items: readonly ActionBarItem[];
  readonly onClear: () => void;
  /** Botões visíveis na web antes de "Mais ações". */
  readonly max?: number;
  readonly className?: string;
}

/**
 * Rodapé da seleção em lote (protótipo `bulk_bar`/`m_bulk`): aparece com os marcados, mostra a
 * contagem e o VOP, e as ações que a tabela libera para aquela seleção. Integra com o `DataView`
 * por props: a tela liga `selection.selected`/`onChange` e põe a barra embaixo da lista.
 */
export function BulkBar({ count, countLabel, summary, items, onClear, max = 3, className }: BulkBarProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  if (count === 0) return null;
  const counter = countLabel ?? (count === 1 ? t`1 selecionado` : t`${count} selecionados`);

  if (isMobile) {
    return (
      <div role="region" aria-label={t`Seleção em lote`} className="contents">
        <MobileActionStrip items={items} {...(className ? { className } : {})}>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium" aria-live="polite">
              {counter}
            </span>
            {summary ? <span className="ml-auto text-sm text-muted-foreground">{summary}</span> : null}
            <Button type="button" variant="ghost" size="sm" className={summary ? '' : 'ml-auto'} onClick={onClear}>
              {t`Limpar`}
            </Button>
          </div>
        </MobileActionStrip>
      </div>
    );
  }

  const { visible, more } = splitActions(items, max);
  return (
    <div
      role="region"
      aria-label={t`Seleção em lote`}
      data-slot="bulk-bar"
      className={cn(
        'sticky bottom-4 z-20 flex flex-wrap items-center gap-3 rounded-[10px] border border-border bg-popover py-2.5 pr-3 pl-4 text-popover-foreground shadow-3 animate-fade-in',
        className,
      )}
    >
      <span aria-hidden className="inline-flex size-[18px] items-center justify-center rounded-[5px] bg-primary text-primary-foreground">
        <Check className="size-3.5" />
      </span>
      <span className="text-sm font-medium" aria-live="polite">
        {counter}
      </span>
      {summary ? <span className="text-sm text-muted-foreground">{summary}</span> : null}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {visible.map((item) => (
          <ActionBarButton key={item.id} item={item} size="sm" />
        ))}
        <MoreActionsMenu items={more} size="sm" />
      </div>
      <Button type="button" variant="ghost" size="icon" aria-label={t`Limpar seleção`} onClick={onClear}>
        <X aria-hidden />
      </Button>
    </div>
  );
}
